import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, Button, Row, Col, Spinner } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { renderShopLogo, renderProductImage } from '../../utils/imageHelpers';
import {
  getShopByName,
  convertUrlToShopName,
  convertShopNameToUrl,
  createOrder,
  getOrderStatus,
  getShopOrders
} from '../../services/shopService';
import { useCart } from '../../contexts/CartContext';
import { useAuth } from '../../contexts/AuthContext';
import { getUserProfile } from '../../services/userService';
import Toast from '../../components/Toast';

const ShopCartPage = () => {
  const { shopName } = useParams();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { cartItems, loading: cartLoading, error: cartError, updateItemQuantity, removeFromCart, updateItemAvailability } = useCart();
  const { currentUser } = useAuth();
  const [shopData, setShopData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [toastVariant, setToastVariant] = useState('success');
  const [isOrdering, setIsOrdering] = useState(false);
  const [orderId, setOrderId] = useState(null);
  const [orderStatus, setOrderStatus] = useState(null);
  const [isCheckingStatus, setIsCheckingStatus] = useState(false);

  useEffect(() => {
    const loadShopData = async () => {
      try {
        setLoading(true);
        const convertedName = convertUrlToShopName(shopName);
        const shop = await getShopByName(convertedName);
        if (!shop) {
          setError('Negozio non trovato');
          return;
        }
        setShopData(shop);
      } catch (err) {
        setError('Errore nel caricamento dei dati del negozio');
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    loadShopData();
  }, [shopName]);

  useEffect(() => {
    // Controlla se c'è un ordine in corso per questo negozio
    const checkExistingOrder = async () => {
      if (!currentUser || !shopData) return;
      
      try {
        setIsCheckingStatus(true);
        const orders = await getShopOrders(shopData.id);
        const pendingOrder = orders.find(order =>
          order.customerId === currentUser.uid &&
          (order.status === 'pending_confirmation' || order.status === 'confirmed') &&
          !order.paymentMethod
        );
        
        if (pendingOrder) {
          setOrderId(pendingOrder.id);
          setOrderStatus(pendingOrder);
          
          // Aggiorna gli items nel carrello con lo stato di disponibilità
          if (pendingOrder.items) {
            pendingOrder.items.forEach(orderItem => {
              const cartItem = cartItems.find(item => item.id === orderItem.id);
              if (cartItem) {
                if (orderItem.isAvailable === false) {
                  updateItemAvailability(cartItem.id, false);
                }
              }
            });
          }
        } else {
          setOrderId(null);
          setOrderStatus(null);
        }
      } catch (error) {
        console.error('Errore nel controllo dello stato dell\'ordine:', error);
      } finally {
        setIsCheckingStatus(false);
      }
    };

    checkExistingOrder();
  }, [currentUser, shopData]);

  // Filtra gli elementi del carrello per questo negozio
  const shopCartItems = shopData ? cartItems.filter(item => item.shopId === shopData.id) : [];

  // Calcola il subtotale dell'ordine
  const calculateSubtotal = () => {
    return shopCartItems.reduce((total, item) =>
      total + (item.isAvailable !== false ? item.price * item.quantity : 0), 0
    );
  };

  // Calcola il costo di spedizione in base alle impostazioni del negozio
  const getShippingCost = () => {
    const shipping = shopData?.shipping;
    if (!shipping) return 0;
    const cost = typeof shipping.cost === 'number' ? shipping.cost : 0;
    // Se il venditore ha impostato costo 0, la spedizione è sempre gratuita
    if (cost === 0) return 0;
    const subtotal = calculateSubtotal();
    const threshold = parseFloat(shipping.freeThreshold) || 0;
    // Se la soglia gratis è impostata e il subtotale la raggiunge, spedizione gratuita
    if (threshold > 0 && subtotal >= threshold) return 0;
    return cost;
  };

  // Gestisce la modifica della quantità
  const handleQuantityChange = (item, newQuantity) => {
    if (newQuantity > 0) {
      updateItemQuantity(item.id, newQuantity);
    }
  };

  // Gestisce la rimozione dell'elemento
  const handleRemoveItem = (itemId) => {
    removeFromCart(itemId);
  };

  const handleOrder = async () => {
    if (!currentUser) {
      navigate('/login');
      return;
    }

    try {
      setIsOrdering(true);
      const shippingCost = getShippingCost();

      // Recupera il profilo cliente per includere telefono, indirizzo, etc.
      let customerProfile = {};
      try {
        customerProfile = await getUserProfile(currentUser.uid) || {};
      } catch (err) {
        console.error('Errore nel recupero del profilo cliente:', err);
      }

      const orderData = {
        customerId: currentUser.uid,
        customerName: currentUser.displayName || customerProfile.name || 'Cliente',
        customerEmail: currentUser.email || customerProfile.email,
        phone: customerProfile.phone || '',
        messagingId: customerProfile.messagingId || '',
        address: [
          customerProfile.address,
          customerProfile.city,
          customerProfile.zipCode
        ].filter(Boolean).join(', '),
        intercom: customerProfile.intercom || '',
        floor: customerProfile.floor || '',
        items: shopCartItems.map(item => ({
          id: item.id,
          name: item.name,
          price: item.price,
          quantity: item.quantity,
          image: item.image
        })),
        total: calculateSubtotal() + shippingCost,
        shippingCost,
        subtotal: calculateSubtotal()
      };

      const order = await createOrder(shopData.id, orderData);
      setOrderId(order.id);
      setOrderStatus(order);
      
      setToastMessage('Ordine creato con successo! In attesa della conferma del venditore.');
      setToastVariant('success');
      setShowToast(true);
    } catch (error) {
      console.error('Errore nella creazione dell\'ordine:', error);
      setToastMessage('Errore nella creazione dell\'ordine');
      setToastVariant('danger');
      setShowToast(true);
    } finally {
      setIsOrdering(false);
    }
  };

  const handlePayment = () => {
    if (!shopData || !orderId) return;
    const shopUrlName = convertShopNameToUrl(shopData.name);
    navigate(`/payment/${shopUrlName}/${orderId}`);
  };

  const renderOrderButton = () => {
    if (isCheckingStatus) {
      return (
        <Button 
          variant="primary" 
          className="w-100 rounded-pill mb-3" 
          style={{ backgroundColor: '#0A0A17', borderColor: '#0A0A17' }}
          disabled
        >
          <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" className="me-2" />
          {t('checkout.checking')}
        </Button>
      );
    }

    if (orderStatus) {
      if (orderStatus.status === 'pending_confirmation') {
        return (
          <Button 
            variant="secondary" 
            className="w-100 rounded-pill mb-3" 
            disabled
          >
            <i className="bi bi-clock me-2"></i>
            {t('checkout.waiting_confirmation')}
          </Button>
        );
      } else if (orderStatus.status === 'confirmed' && orderStatus.paymentStatus === 'pending') {
        return (
          <Button 
            variant="primary" 
            className="w-100 rounded-pill mb-3" 
            style={{ backgroundColor: '#0A0A17', borderColor: '#0A0A17' }}
            onClick={handlePayment}
          >
            <i className="bi bi-credit-card me-2"></i>
            {t('checkout.pay_now')}
          </Button>
        );
      }
    }

    return (
      <Button 
        variant="primary" 
        className="w-100 rounded-pill mb-3" 
        style={{ backgroundColor: '#0A0A17', borderColor: '#0A0A17' }}
        onClick={handleOrder}
        disabled={isOrdering}
      >
        {isOrdering ? (
          <>
            <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" className="me-2" />
            {t('checkout.processing')}
          </>
        ) : (
          t('checkout.order')
        )}
      </Button>
    );
  };

  if (loading || cartLoading) {
    return (
      <div className="text-center py-5">
        <Spinner animation="border" role="status" />
      </div>
    );
  }

  if (error || cartError) {
    return <div className="text-center py-5 text-danger">{error || cartError}</div>;
  }

  if (!shopData) {
    return <div className="text-center py-5">Negozio non trovato</div>;
  }

  if (!currentUser) {
    return (
      <div className="text-center py-5">
        <p>Devi effettuare il login per vedere il carrello</p>
        <Button variant="primary" onClick={() => navigate('/login')}>
          Accedi
        </Button>
      </div>
    );
  }

  if (shopCartItems.length === 0) {
    return (
      <div className="py-2">
        <Button 
          variant="link" 
          className="text-decoration-none p-0 mb-3 d-flex align-items-center text-secondary"
          onClick={() => navigate('/checkout')}
        >
          <i className="bi bi-arrow-left me-2"></i>
          {t('checkout.back_to_cart')}
        </Button>
        
        <div className="text-center py-5">
          <i className="bi bi-cart-x" style={{ fontSize: 80, color: '#e0e0e0' }}></i>
          <div className="mt-3 text-muted" style={{ fontSize: 20 }}>
            {t('checkout.empty_cart') || 'Il carrello è vuoto'}
          </div>
          <Button 
            variant="primary" 
            className="mt-3"
            onClick={() => navigate(`/${shopName}`)}
          >
            Continua lo shopping
          </Button>
        </div>
      </div>
    );
  }

  // Pre-calcolo i totali una volta sola per coerenza in tutto il render
  const subtotal = calculateSubtotal();
  const shippingCost = getShippingCost();
  const total = subtotal + shippingCost;

  return (
    <div className="py-2 position-relative">
      <Toast
        show={showToast}
        onClose={() => setShowToast(false)}
        message={toastMessage}
        variant={toastVariant}
      />

      {/* Tasto indietro coerente con dettaglio prodotto */}
      <div className="mb-3" style={{ display: 'flex', justifyContent: 'flex-start' }}>
        <Button
          variant="dark"
          className="rounded-circle shadow-sm border-0"
          style={{ width: 44, height: 44, padding: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
          onClick={() => navigate('/checkout')}
        >
          <i className="bi bi-arrow-left fs-4 text-white"></i>
        </Button>
      </div>
      {/* Header Shop */}
      <Card className="mb-4 rounded-4 shop-card">
        <Card.Body>
          <div className="d-flex align-items-start mb-3">
            <div className="rounded-circle overflow-hidden bg-white d-flex align-items-center justify-content-center" style={{ width: 60, height: 60, minWidth: 60, flexShrink: 0 }}>
              {renderShopLogo(shopData.logoUrl, shopData.name, 60)}
            </div>
            <div className="ms-3 flex-grow-1">
              <div className="d-flex flex-column">
                <h5 className="fw-bold mb-1">{shopData.name}</h5>
                {shopData.subtitle && <p className="text-muted small mb-2">{shopData.subtitle}</p>}
                <div className="d-flex flex-wrap gap-3">
                  <div className="d-flex align-items-center">
                    <i className="bi bi-geo-alt me-1 text-primary small"></i>
                    <span className="small">{shopData.address?.street}, {shopData.address?.city}</span>
                  </div>
                  <div className="d-flex align-items-center">
                    <i className="bi bi-clock me-1 text-primary small"></i>
                    <span className="small">{shopData.openingHours || 'Orari non disponibili'}</span>
                  </div>
                  <div className="d-flex align-items-center">
                    <i className="bi bi-telephone me-1 text-primary small"></i>
                    <span className="small">{shopData.contact?.phone || 'Non disponibile'}</span>
                  </div>
                  <div className="d-flex align-items-center">
                    <i className="bi bi-wechat me-1 text-primary small"></i>
                    <span className="small">{shopData.contact?.wechatId || 'Non disponibile'}</span>
                  </div>
                </div>
              </div>
            </div>
            {orderStatus && (
              <div className="ms-3 text-end">
                <p className="text-muted small mb-0">Ordine #{orderStatus.orderNumber?.toString().padStart(6, '0')}</p>
              </div>
            )}
          </div>
        </Card.Body>
      </Card>

      {/* Prodotti */}
      <h5 className="mb-3 fw-bold">{t('checkout.cart_products')}</h5>
      <Card className="mb-4 rounded-4 shop-card">
        <Card.Body>
          {shopCartItems.map((item, index) => (
            <React.Fragment key={item.id}>
              {index > 0 && <hr className="my-3" />}
              <Row className="align-items-center">
                <Col xs={3}>
                  <div className="rounded-4 overflow-hidden bg-white position-relative" style={{ width: '70px', height: '70px' }}>
                    {renderProductImage(item.image, item.name)}
                    {item.isAvailable === false && (
                      <div
                        className="position-absolute w-100 h-100 d-flex align-items-center justify-content-center"
                        style={{
                          top: 0,
                          left: 0,
                          backgroundColor: 'rgba(0, 0, 0, 0.5)',
                          backdropFilter: 'blur(2px)'
                        }}
                      >
                        <i className="bi bi-slash-circle text-white" style={{ fontSize: '1.5rem' }}></i>
                      </div>
                    )}
                  </div>
                </Col>
                <Col xs={6}>
                  <p className="mb-0 fw-medium text-dark" style={{ opacity: item.isAvailable === false ? 0.5 : 1 }}>{item.name}</p>
                  <p className="text-primary mb-0 fw-bold" style={{ opacity: item.isAvailable === false ? 0.5 : 1 }}>€{item.price.toFixed(2)}</p>
                  <div className="d-flex align-items-center mt-2">
                    <div style={{ background: '#F2F2F2', borderRadius: 32, display: 'flex', alignItems: 'center', padding: '4px 4px' }}>
                      <Button
                        variant="light"
                        className="rounded-circle border-0 d-flex align-items-center justify-content-center"
                        style={{ width: 32, height: 32, fontSize: 16, padding: 0 }}
                        onClick={() => handleQuantityChange(item, item.quantity - 1)}
                        disabled={item.quantity <= 1 || item.isAvailable === false}
                      >
                        <i className="bi bi-dash"></i>
                      </Button>
                      <span className="mx-2 fw-bold" style={{ fontSize: 14, minWidth: 20, textAlign: 'center' }}>{item.quantity}</span>
                      <Button
                        variant="light"
                        className="rounded-circle border-0 d-flex align-items-center justify-content-center"
                        style={{ width: 32, height: 32, fontSize: 16, padding: 0 }}
                        onClick={() => handleQuantityChange(item, item.quantity + 1)}
                        disabled={item.isAvailable === false}
                      >
                        <i className="bi bi-plus"></i>
                      </Button>
                    </div>
                    <Button
                      variant="light"
                      className="rounded-circle border-0 d-flex align-items-center justify-content-center ms-2"
                      style={{ width: 40, height: 40, fontSize: 16, padding: 0, backgroundColor: '#F2F2F2' }}
                      onClick={() => handleRemoveItem(item.id)}
                      disabled={item.isAvailable === false}
                    >
                      <i className="bi bi-trash text-danger"></i>
                    </Button>
                  </div>
                </Col>
                <Col xs={3} className="text-end">
                  <p className="fw-bold mb-0 text-dark" style={{ opacity: item.isAvailable === false ? 0.5 : 1 }}>€{(item.price * item.quantity).toFixed(2)}</p>
                </Col>
              </Row>
            </React.Fragment>
          ))}
        </Card.Body>
      </Card>

      {/* Dettagli Ordine */}
      <h5 className="mb-3 fw-bold">{t('checkout.order_summary')}</h5>
      <Card className="mb-4 rounded-4 shop-card">
        <Card.Body>
          <div className="d-flex justify-content-between mb-2">
            <span className="text-secondary">{t('checkout.subtotal')}</span>
            <span className="text-dark">€{subtotal.toFixed(2)}</span>
          </div>
          <div className="d-flex justify-content-between mb-2">
            <span className="text-secondary">{t('checkout.shipping')}</span>
            <span className="text-dark">
              {shippingCost === 0 ? (
                <span className="text-success fw-medium">Gratuita</span>
              ) : (
                `€${shippingCost.toFixed(2)}`
              )}
            </span>
          </div>
          <hr />
          <div className="d-flex justify-content-between">
            <span className="fw-bold text-dark">{t('checkout.total')}</span>
            <span className="fw-bold text-dark">€{total.toFixed(2)}</span>
          </div>
        </Card.Body>
      </Card>

      {renderOrderButton()}
    </div>
  );
};

export default ShopCartPage; 